import {
   ChangeDetectionStrategy,
   Component,
   EventEmitter,
   Input,
   OnDestroy,
   Output,
   ViewChild,
   ViewEncapsulation,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { CallState } from '@lcs/shared/api';
import { Actions, ofType } from '@ngrx/effects';
import { ExtensionTypes, PromptTypes } from 'generated/graphql-types';
import { Subject } from 'rxjs';
import { UserDetailLinkModel } from 'src/app/common-components/detail-links/models/user-detail-link.model';
import { NavigatableDetailView } from 'src/app/common-components/detail/detail-view';
import { getExtensionDetailSaveEvent } from 'src/app/common-components/helpers/extension-form-helper';
import { CallDetailsPrivileges } from '../../call-logs-register/models/call-details-privileges';
import { DepartmentPrivileges } from '../../department-register/models/department-privileges';
import { VoiceMailGreetingPromptOption } from '../../mailbox-details/models/voicemail-greeting-prompt-option.model';
import { MailboxDetailComponent } from '../../mailbox-details/views/mailbox-detail-sections/mailbox-detail-view.component';
import { PhonePrivileges } from '../../phones/models/phone-privileges';
import { PromptPrivileges } from '../../prompt-register/models/prompt-privileges';
import { UserOption } from '../../user-register/models/user-option.interface';
import { ExtensionHackedFraudDetails } from '../helpers/hacked-fraud-helper';
import { AddExistingPromptEvent } from '../models/add-existing-prompt-event.model';
import { AddNewPromptEvent } from '../models/add-new-prompt-event.model';
import { ExtensionDetailSaveEvent } from '../models/extension-detail-form-submit-event';
import {
   ExtensionDetailView,
   ExtensionNumberDetail,
   ExternalNumberDetail,
} from '../models/extension-detail-view.model';
import { ExtensionPrivileges } from '../models/extension-privileges';
import {
   GetExtensionDetail_extension_createUser,
   GetExtensionDetail_extension_mailbox,
   GetExtensionDetail_extension_updateUser,
} from '../queries/generated/GetExtensionDetail';
import { ExtensionDetailFormService } from '../services/extension-detail-form.service';
import * as ExtensionDetailActions from '../state/extension-detail.actions';

@Component({
   changeDetection: ChangeDetectionStrategy.OnPush,
   encapsulation: ViewEncapsulation.None,
   providers: [{ multi: true, provide: NG_VALUE_ACCESSOR, useExisting: ExtensionDetailViewComponent }],
   selector: 'ndt-extension-detail-view',
   styleUrls: ['./extension-detail-view.component.scss'],
   templateUrl: './extension-detail-view.component.html',
   standalone: false,
})
export class ExtensionDetailViewComponent extends NavigatableDetailView implements OnDestroy {
   @Input() get extension(): ExtensionDetailView | null {
      return this._extension;
   }

   @Input() hackedFraudDetails: ExtensionHackedFraudDetails | null;

   set extension(value: ExtensionDetailView | null) {
      const previousExtension = this._extension;
      this._extension = value;
      if (value) {
         this.formService.updateExtension(value);
         this.unreadMessageCount = value.mailbox?.mailboxMessagesConnection.items.filter((item) => {
            return !item.isRead && !item.isArchived;
         }).length;
         if (!previousExtension?.type?.name) {
            this.modifyEditState();
            this.form.get('extensionNotes')?.enable();
         }

         this.setCreateAndUpdateUser(value.createUser, value.updateUser);
      }
   }

   @Input() callState: CallState;

   @Output() formSaveCanceled = new EventEmitter<void>();

   @Output() formSubmitted = new EventEmitter<ExtensionDetailSaveEvent>();

   @Output() deleteExtension = new EventEmitter<{
      extensionId: number;
      number: number;
      extensionType: string;
      autoAttendantsUsingForTimeout?: ExtensionDetailView[];
      fmFmsUsingForFinalDestination?: ExtensionDetailView[];
      queuesUsingForExitRoute?: ExtensionDetailView[];
      huntGroupsUsingForRouteBusy?: ExtensionDetailView[];
      huntGroupsUsingForRouteNoAnswer?: ExtensionDetailView[];
   }>();

   @Output() addExistingPrompt = new EventEmitter<AddExistingPromptEvent>();

   @Output() addNewPrompt = new EventEmitter<AddNewPromptEvent>();

   @Output() addNewTimeBasedRoute = new EventEmitter<void>();

   @Output() addNewFindMeFollowMeStep = new EventEmitter<void>();

   @Output() editMailboxPin = new EventEmitter<void>();

   @Output() resetHackedAndFraud = new EventEmitter<ExtensionHackedFraudDetails>();

   @Output() changeDNDStatus = new EventEmitter<string | null>();

   @Output() resetMwi = new EventEmitter<any>();

   @Output() clearMailboxClick = new EventEmitter<number[]>();
   @Output() resetMailboxClick = new EventEmitter<{
      mailboxId: number;
      mailboxMessageIds: number[];
   }>();

   @ViewChild(MailboxDetailComponent)
   private mailboxDetail: MailboxDetailComponent;

   @Input() phonePrivileges: PhonePrivileges;

   @Input() callDetailsPrivileges: CallDetailsPrivileges;

   @Input() extensionNumbers: ExtensionNumberDetail[];

   @Input() externalNumbers: ExternalNumberDetail[];

   extensionTypes: typeof ExtensionTypes = ExtensionTypes;

   promptTypes: typeof PromptTypes = PromptTypes;

   unreadMessageCount: number | undefined;

   selectedIndex: number;

   searchControl = new UntypedFormControl('');

   loadSuccess$ = this.actions$.pipe(ofType(ExtensionDetailActions.loadExtensionDetailSuccess));

   callLogTabLabel = 'Call Log';

   private destroyed$ = new Subject<void>();

   @Input() set extensionPrivileges(privileges: ExtensionPrivileges) {
      this._extensionPrivileges = privileges;
      this.modifyEditState();
   }

   @Input() set promptPrivileges(privileges: PromptPrivileges) {
      this._promptPrivileges = privileges;
   }

   @Input() set departmentPrivileges(privileges: DepartmentPrivileges) {
      this._departmentPrivileges = privileges;
   }

   get extensionPrivileges(): ExtensionPrivileges {
      return this._extensionPrivileges;
   }

   get departmentPrivileges(): DepartmentPrivileges {
      return this._departmentPrivileges;
   }

   form: UntypedFormGroup;

   callLogsLoaded: boolean = false;

   _extension: ExtensionDetailView | null;

   _extensionPrivileges: ExtensionPrivileges;

   _departmentPrivileges: DepartmentPrivileges;

   _promptPrivileges: PromptPrivileges;

   _createUser: UserDetailLinkModel | null;

   _updateUser: UserDetailLinkModel | null;

   get users(): UntypedFormArray {
      const users = this.form.get('users') as UntypedFormArray;
      return users;
   }

   get entityId(): string | number | null {
      return this.extension?.extensionId ?? null;
   }

   constructor(public formService: ExtensionDetailFormService, private actions$: Actions) {
      super();
      this.form = this.formService.form;

      this.resetHackedAndFraud = new EventEmitter<ExtensionHackedFraudDetails>();
   }

   resetHackedAndFraudDetails() {
      this.resetHackedAndFraud.emit(this.hackedFraudDetails ?? undefined);
   }

   onTabChange(newTab: MatTabChangeEvent) {
      if (newTab.tab.textLabel === this.callLogTabLabel) {
         this.callLogsLoaded = true;
      }
   }

   ngOnChanges(): void {
      const otherExtensionNumbers: number[] = this.extensionNumbers
         .filter((ext) => ext.number !== this.extension?.number)
         .map((ext) => ext.number)
         .filter((num): num is number => num !== undefined);

      this.formService.addUniqueExtensionNumberValidator(otherExtensionNumbers);
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   trackBy(item: UserOption | null) {
      return item?.userId;
   }

   onSubmit(): void {
      if (this.form.valid) {
         const submitEvent = this.getFormSubmittedEvent();
         if (submitEvent) {
            this.formSubmitted.emit(submitEvent);
         }
      }
   }

   onCancel(): void {
      this.formSaveCanceled.emit();
   }

   onDeleteClick(): void {
      if (this.extension) {
         this.deleteExtension.emit({
            extensionId: this.extension.extensionId,
            extensionType: this.extension.type?.name ?? '',
            number: this.extension.number ?? 0,
            autoAttendantsUsingForTimeout: this.extension.autoAttendantsUsingForTimeout ?? [],
            fmFmsUsingForFinalDestination: this.extension.fmFmsUsingForFinalDestination ?? [],
            queuesUsingForExitRoute: this.extension.queuesUsingForExitRoute ?? [],
            huntGroupsUsingForRouteBusy: this.extension.huntGroupsUsingForRouteBusy ?? [],
            huntGroupsUsingForRouteNoAnswer: this.extension.huntGroupsUsingForRouteNoAnswer ?? [],
         });
      }
   }

   onResetMwiClick(): void {
      if (this.extension) {
         this.resetMwi.emit(this.extension.extensionId);
      }
   }

   onAddExistingAutoAttendantPromptClick(): void {
      this.addExistingPrompt.emit({ promptTypes: [PromptTypes.AutoAttendant] });
   }

   onAddExistingPeriodicAnnouncementPromptClick(): void {
      this.addExistingPrompt.emit({ promptTypes: [PromptTypes.PeriodicAnnouncement] });
   }

   onAddNewAutoAttendantPromptClick(): void {
      this.addNewPrompt.emit({ promptType: PromptTypes.AutoAttendant, voiceMailGreetingPrompt: null });
   }

   onAddNewPeriodicAnnouncementClick(): void {
      this.addNewPrompt.emit({ promptType: PromptTypes.PeriodicAnnouncement, voiceMailGreetingPrompt: null });
   }

   onAddNewVoiceMailGreetingPromptClick(event: VoiceMailGreetingPromptOption | null): void {
      this.addNewPrompt.emit({
         promptType: PromptTypes.VoiceMailGreeting,
         voiceMailGreetingPrompt: event,
      });
   }

   onEditMailboxPinClick(mailbox: GetExtensionDetail_extension_mailbox | null | undefined): void {
      this.editMailboxPin.emit(mailbox?.mailboxId);
   }

   onAddTimeBasedRouteClick(): void {
      this.addNewTimeBasedRoute.emit();
   }

   onAddFindMeFollowMeStepClick(): void {
      this.addNewFindMeFollowMeStep.emit();
   }

   onChangeDNDStatus(dndStatus: string | null) {
      this.changeDNDStatus.emit(dndStatus);
   }

   clearMailbox() {
      const ids = this.mailboxDetail?.getMailboxMessageIds() ?? [];
      this.clearMailboxClick.emit(ids);
   }

   resetMailbox() {
      const ids = this.mailboxDetail?.getMailboxMessageIds() ?? [];
      const mailboxId = this.mailboxDetail?.getMailboxId();
      this.resetMailboxClick.emit({
         mailboxId,
         mailboxMessageIds: ids,
      });
   }

   modifyEditState() {
      const canEdit = this.canEdit();
      if (!canEdit) {
         this.form.disable();
      } else if (canEdit) {
         this.form.enable();
      }
   }

   canEdit(): boolean {
      if (this._extensionPrivileges)
         switch (this._extension?.type?.name) {
            case ExtensionTypes.Phone:
               return this.extensionPrivileges.canEditPhones;
            case ExtensionTypes.AutoAttendant:
               return this.extensionPrivileges.canEditAutoAttendants;
            case ExtensionTypes.Fax:
               return this.extensionPrivileges.canEditFaxes;
            default:
               return this.extensionPrivileges.canEditOthers;
         }

      return false;
   }

   canDelete(): boolean {
      if (this._extension?.type?.name === ExtensionTypes.SmartVoicemail) {
         return false;
      }

      if (this._extensionPrivileges)
         switch (this._extension?.type?.name) {
            case ExtensionTypes.Phone:
               return this.extensionPrivileges.canDeletePhones;
            case ExtensionTypes.AutoAttendant:
               return this.extensionPrivileges.canDeleteAutoAttendants;
            case ExtensionTypes.Fax:
               return this.extensionPrivileges.canDeleteFaxes;
            default:
               return this.extensionPrivileges.canDeleteOthers;
         }

      return false;
   }

   onAnnounceTimeOptionChange(checkbox: MatCheckboxChange) {
      if (
         !this.form.get('extensionQueue.isAnnounceWaitTime')?.value &&
         !this.form.get('extensionQueue.isAnnounceQueueCount')?.value &&
         !this.form.get('extensionQueue.isAnnounceCallback')?.value
      ) {
         this.form.get('extensionQueue.announcementFrequency')?.removeValidators(Validators.required);
      } else {
         this.form.get('extensionQueue.announcementFrequency')?.addValidators(Validators.required);
      }
      this.form.updateValueAndValidity();
   }

   private getFormSubmittedEvent(): ExtensionDetailSaveEvent | null {
      if (!this.formService.form.dirty || !this.extension) {
         return null;
      }
      const extension = this.formService.getValue(this.extension.extensionId);

      return getExtensionDetailSaveEvent(extension, this.formService);
   }

   canResetMailbox(): boolean {
      const ids = this.mailboxDetail?.getMailboxMessageIds() ?? [];
      const mailboxHasPrompts = this.mailboxDetail?.mailboxHasPrompts();
      return ids.length || mailboxHasPrompts ? true : false;
   }

   canClearMailbox(): boolean {
      const ids = this.mailboxDetail?.getMailboxMessageIds() ?? [];
      return ids.length > 0;
   }

   private setCreateAndUpdateUser(
      createUser: GetExtensionDetail_extension_createUser | null | undefined,
      updateUser: GetExtensionDetail_extension_updateUser | null | undefined
   ) {
      this._createUser = {
         name: createUser?.name ?? '',
         userId: createUser?.userId ?? 0,
      };

      this._updateUser = {
         name: updateUser?.name ?? '',
         userId: updateUser?.userId ?? 0,
      };
   }
}
